import { useEffect, useState } from 'react';
import { apiFetch, resolveMediaUrl } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { MicroPromotionDaysCell } from '@/components/MicroPromotionDaysCell';
import {
  MicroPromotionRobotProgressCell,
  type RobotProgressItem,
} from '@/components/MicroPromotionRobotProgressCell';
import {
  MicroPromotionReviewAlert,
  mpServingStatusLabel,
  type MpReviewReason,
} from '@/lib/microPromotionReviewUi';
import { mpEntryPreviewText } from '@/lib/microPromotionEntryText';

type ImageItem = {
  image_id?: number;
  path?: string;
  source_url?: string;
  local_path?: string;
  preview_url?: string;
  download_url?: string;
  url?: string;
};

type StructuredField = {
  label: string;
  value: string;
};

export type MicroPromotionEntryDetail = {
  entry_id: number;
  info_type: string;
  source_label?: string;
  entry_source?: string;
  promotion_serving_status?: string;
  review_reason?: MpReviewReason;
  matched_sensitive_words?: string[];
  sensitive_review_detail?: string;
  form_code?: string | null;
  form_name?: string | null;
  serial_number?: string | null;
  trade_no?: string | null;
  trade_status?: string | null;
  display_text?: string;
  promotion_text?: string;
  job_title?: string;
  salary?: string;
  recruiter?: string;
  job_description?: string;
  forward_text?: string;
  forward_content_template?: string;
  structured_fields?: StructuredField[];
  images?: ImageItem[];
  default_moments_images?: ImageItem[];
  promotion_moments_enabled?: boolean;
  total_promotion_days?: number;
  promotion_channel?: string;
  moments_planned_days?: number;
  groups_planned_days?: number;
  robot_progress?: RobotProgressItem[];
  promotion_start_date?: string;
  creator_name?: string | null;
  weixin_nickname?: string | null;
  contact_phone?: string;
  created_at?: string;
  package_items?: Array<{ name?: string; number?: number }>;
  total_price?: number;
  preferential_price?: number;
  order_no?: string | null;
  payable_amount?: number;
  actual_paid_amount?: number;
  payment_method?: string | null;
  payment_method_label?: string | null;
  device_info?: string | null;
};

function formatYuan(n: number): string {
  const v = Number(n);
  if (!Number.isFinite(v)) {
    return '0.00';
  }
  return v.toFixed(2);
}

type Props = {
  open: boolean;
  entryId: number;
  onClose: () => void;
  onReviewChanged?: () => void;
  siteMasterMode?: boolean;
};

function imageSrc(im: ImageItem): string {
  return (
    im.preview_url ||
    im.download_url ||
    im.url ||
    (im.path ? resolveMediaUrl(im.path) : '') ||
    (im.local_path ? resolveMediaUrl(im.local_path) : '') ||
    im.source_url ||
    ''
  );
}

function ImageGrid({ items, caption }: { items: ImageItem[]; caption?: string }) {
  if (items.length === 0) return null;
  return (
    <div>
      {caption ? <p className="mb-2 text-xs font-medium text-slate-600">{caption}</p> : null}
      <div className="flex flex-wrap gap-2">
        {items.map((im, idx) => {
          const src = imageSrc(im);
          const href = im.download_url || src;
          return (
            <a
              key={im.image_id ?? im.path ?? idx}
              href={href || undefined}
              target="_blank"
              rel="noreferrer"
              className="block h-20 w-20 overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
            >
              {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null}
            </a>
          );
        })}
      </div>
    </div>
  );
}

export function MicroPromotionEntryDetailModal({
  open,
  entryId,
  onClose,
  onReviewChanged,
  siteMasterMode = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [entry, setEntry] = useState<MicroPromotionEntryDetail | null>(null);

  useEffect(() => {
    if (!open || entryId < 1) {
      setEntry(null);
      setErr('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr('');
    void (async () => {
      try {
        const action = siteMasterMode ? 'site_master_get_mp_entry' : 'get_micro_promotion_entry';
        const r = await apiFetch<{ ok: boolean; entry?: MicroPromotionEntryDetail; err?: string }>(
          action,
          { method: 'GET', query: { entry_id: entryId } }
        );
        if (cancelled) return;
        if (!r.ok || !r.entry) {
          setErr('\u52a0\u8f7d\u8be6\u60c5\u5931\u8d25');
          setEntry(null);
          return;
        }
        setEntry(r.entry);
      } catch {
        if (!cancelled) setErr('\u52a0\u8f7d\u8be6\u60c5\u5931\u8d25');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, entryId, siteMasterMode]);

  const status = entry?.promotion_serving_status || 'active';
  const statusLabel = mpServingStatusLabel(status);
  const isManualLike =
    entry?.entry_source === 'manual' || entry?.entry_source === 'site_master' || siteMasterMode;

  async function approveReview() {
    if (!entry) return;
    setReviewBusy(true);
    try {
      const action = siteMasterMode
        ? 'site_master_approve_micro_promotion_entry_review'
        : 'approve_micro_promotion_entry_review';
      const r = await apiFetch<{ ok: boolean }>(action, {
        method: 'POST',
        body: JSON.stringify({ entry_id: entry.entry_id }),
      });
      if (r.ok) {
        onReviewChanged?.();
        onClose();
      }
    } finally {
      setReviewBusy(false);
    }
  }

  async function rejectReview() {
    if (!entry || !window.confirm('确定拒绝该条审核？')) return;
    setReviewBusy(true);
    try {
      const action = siteMasterMode
        ? 'site_master_reject_micro_promotion_entry_review'
        : 'reject_micro_promotion_entry_review';
      const r = await apiFetch<{ ok: boolean }>(action, {
        method: 'POST',
        body: JSON.stringify({ entry_id: entry.entry_id }),
      });
      if (r.ok) {
        onReviewChanged?.();
        onClose();
      }
    } finally {
      setReviewBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={entry ? `\u63a8\u5e7f\u8be6\u60c5 #${entry.entry_id}` : '\u63a8\u5e7f\u8be6\u60c5'}
      wide="xl"
      onClose={onClose}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {status === 'pending_review' ? (
            <>
              <Button type="button" variant="primary" disabled={reviewBusy} onClick={() => void approveReview()}>
                通过审核
              </Button>
              <Button type="button" variant="ghost" disabled={reviewBusy} className="text-rose-700" onClick={() => void rejectReview()}>
                拒绝
              </Button>
            </>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose}>
            {'\u5173\u95ed'}
          </Button>
        </div>
      }
    >
      {loading ? (
        <Spinner label="加载详情" />
      ) : err ? (
        <p className="text-sm text-rose-600">{err}</p>
      ) : entry ? (
        siteMasterMode ? (
          <div className="max-h-[min(75vh,680px)] space-y-4 overflow-y-auto pr-1">
            <MicroPromotionReviewAlert
              servingStatus={status}
              reviewReason={entry.review_reason}
              matchedWords={entry.matched_sensitive_words}
            />
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700">
                {entry.source_label || '金数据'}
              </span>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700">{statusLabel}</span>
            </div>

            <dl className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
              {(entry.structured_fields || []).map((f) => (
                <div key={f.label}>
                  <dt className="text-sm font-medium text-slate-500">{f.label}</dt>
                  <dd className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-slate-900">{f.value || '—'}</dd>
                </div>
              ))}
            </dl>

            <div className="rounded-2xl border border-teal-200/80 bg-teal-50/50 p-4">
              <h3 className="text-base font-semibold text-teal-900">机器人转发预览</h3>
              <p className="mt-1 text-sm text-slate-600">按区域模板生成，发朋友圈/群发时使用</p>
              <pre className="mt-3 whitespace-pre-wrap rounded-xl border border-teal-200/60 bg-white p-3 text-base leading-relaxed text-slate-800">
                {entry.forward_text || mpEntryPreviewText(entry) || '—'}
              </pre>
            </div>

            {(entry.images?.length ?? 0) > 0 ? (
              <ImageGrid items={entry.images || []} caption="配图" />
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              <p>
                推广 {entry.total_promotion_days ?? 0} 天 · 起始 {entry.promotion_start_date || '—'}
              </p>
              {entry.created_at ? <p className="mt-1 text-slate-500">提交于 {entry.created_at}</p> : null}
            </div>
          </div>
        ) : (
        <div className="max-h-[min(70vh,640px)] space-y-5 overflow-y-auto pr-1 text-sm">
          <MicroPromotionReviewAlert
            servingStatus={status}
            reviewReason={entry.review_reason}
            matchedWords={entry.matched_sensitive_words}
          />
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700">
              {entry.source_label || '\u91d1\u6570\u636e'}
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700">{statusLabel}</span>
            {entry.serial_number ? (
              <span className="font-mono text-slate-500">{'\u5e8f\u53f7'} {entry.serial_number}</span>
            ) : null}
            {entry.form_name ? <span className="text-slate-500">{entry.form_name}</span> : null}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900">{'\u8ba2\u5355\u4fe1\u606f'}</h3>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              {!isManualLike ? (
              <>
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u8ba2\u5355\u7f16\u53f7'}</dt>
                <dd className="mt-0.5 font-mono text-sm text-slate-900">{entry.order_no || '\u2014'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u4ed8\u6b3e\u65b9\u5f0f'}</dt>
                <dd className="mt-0.5 text-sm text-slate-900">
                  {entry.payment_method_label || entry.payment_method || '\u2014'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u5e94\u4ed8'}</dt>
                <dd className="mt-0.5 font-mono tabular-nums text-sm text-slate-900">
                  {'\u00a5'}
                  {formatYuan(Number(entry.payable_amount ?? entry.total_price ?? 0))}
                  {Number(entry.preferential_price ?? 0) > 0 ? (
                    <span className="ml-2 text-xs font-sans text-slate-500">
                      {'\u5df2\u51cf\u4f18\u60e0 \u00a5'}
                      {formatYuan(Number(entry.preferential_price))}
                    </span>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u5b9e\u4ed8'}</dt>
                <dd
                  className={`mt-0.5 font-mono tabular-nums text-sm ${
                    Number(entry.actual_paid_amount ?? 0) > 0 ? 'font-semibold text-emerald-700' : 'text-slate-600'
                  }`}
                >
                  {'\u00a5'}
                  {formatYuan(Number(entry.actual_paid_amount ?? 0))}
                  {entry.entry_source !== 'manual' && entry.trade_status ? (
                    <span className="ml-2 text-xs font-sans font-normal text-slate-500">
                      {String(entry.trade_status).toUpperCase().includes('SUCCESS') ? '\u5df2\u652f\u4ed8' : '\u672a\u652f\u4ed8\u6210\u529f'}
                    </span>
                  ) : null}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-slate-500">{'\u7528\u6237\u8bbe\u5907\u4fe1\u606f'}</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-sm text-slate-900">{entry.device_info || '\u2014'}</dd>
              </div>
              </>
              ) : null}
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u63a8\u5e7f\u5468\u671f'}</dt>
                <dd className="mt-0.5 text-sm text-slate-900">
                  {entry.total_promotion_days ?? 0} {'\u5929 \u00b7 \u8d77\u59cb'} {entry.promotion_start_date || '\u2014'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u63a8\u5e7f\u5929\u6570'}</dt>
                <dd className="mt-1">
                  <MicroPromotionDaysCell row={entry} />
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-slate-500">{'\u4efb\u52a1\u5b8c\u6210\u60c5\u51b5'}</dt>
                <dd className="mt-1">
                  <MicroPromotionRobotProgressCell
                    items={entry.robot_progress}
                    promotionChannel={entry.promotion_channel}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u63d0\u4ea4\u4eba'}</dt>
                <dd className="mt-0.5 text-sm text-slate-900">{entry.creator_name || '\u2014'}</dd>
              </div>
              {entry.weixin_nickname ? (
                <div>
                  <dt className="text-xs font-medium text-slate-500">{'\u5fae\u4fe1\u6635\u79f0'}</dt>
                  <dd className="mt-0.5 text-sm text-slate-900">{entry.weixin_nickname}</dd>
                </div>
              ) : null}
              <div>
                <dt className="text-xs font-medium text-slate-500">{'\u5165\u5e93\u65f6\u95f4'}</dt>
                <dd className="mt-0.5 text-sm text-slate-900">{entry.created_at || '\u2014'}</dd>
              </div>
              {(entry.package_items?.length ?? 0) > 0 ? (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium text-slate-500">{'\u5957\u9910'}</dt>
                  <dd className="mt-0.5 text-sm text-slate-900">
                    <ul className="list-inside list-disc space-y-0.5">
                      {entry.package_items!.map((p, i) => (
                        <li key={i}>
                          {p.name ?? '\u5546\u54c1'} {'\u00d7'} {p.number ?? 1}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>

          <dl className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4 sm:grid-cols-2">
            {(entry.structured_fields || []).map((f) => (
              <div key={f.label} className={f.label === '\u5de5\u4f5c\u5185\u5bb9' ? 'sm:col-span-2' : ''}>
                <dt className="text-xs font-medium text-slate-500">{f.label}</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-slate-900">{f.value || '\u2014'}</dd>
              </div>
            ))}
          </dl>

          <div className="rounded-xl border border-teal-200/80 bg-teal-50/40 p-4">
            <h3 className="text-sm font-semibold text-teal-900">Android {'\u8f6c\u53d1\u6b63\u6587\uff08\u6309\u6a21\u677f\u751f\u6210\uff09'}</h3>
            <p className="mt-1 text-xs text-teal-800/80">
              {'\u6839\u636e\u5f53\u524d\u533a\u57df\u300c\u53c2\u6570\u914d\u7f6e\u300d\u4e2d\u7684\u8f6c\u53d1\u5185\u5bb9\u6a21\u677f\u5c55\u5f00\u5360\u4f4d\u7b26\u540e\u7684\u6700\u7ec8\u7eaf\u6587\u5b57\uff08\u7fa4\u53d1\u4ec5\u53d1\u6b64\u6bb5\u6587\u5b57\uff09\u3002'}
            </p>
            {entry.forward_content_template ? (
              <details className="mt-2 text-xs text-slate-600">
                <summary className="cursor-pointer text-slate-500">{'\u67e5\u770b\u539f\u59cb\u6a21\u677f'}</summary>
                <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-2 font-mono text-[11px] text-slate-700">
                  {entry.forward_content_template}
                </pre>
              </details>
            ) : (
              <p className="mt-1 text-xs text-slate-500">{'\u672a\u914d\u7f6e\u6a21\u677f\uff0c\u4ee5\u4e0b\u4e3a\u7cfb\u7edf\u6c47\u603b\u5c55\u793a\u6587\u6848\u3002'}</p>
            )}
            <pre className="mt-3 whitespace-pre-wrap rounded-lg border border-teal-200/60 bg-white p-3 font-sans text-sm leading-relaxed text-slate-800">
              {entry.forward_text || mpEntryPreviewText(entry) || '\u2014'}
            </pre>
          </div>

          {entry.promotion_moments_enabled !== false ? (
            <ImageGrid
              items={entry.default_moments_images || []}
              caption={'\u53d1\u5708\u9ed8\u8ba4\u914d\u56fe\uff08\u53c2\u6570\u914d\u7f6e \u00b7 \u672c\u6761\u65e0\u9644\u56fe\u65f6\u4f7f\u7528\uff09'}
            />
          ) : null}

          {(entry.images?.length ?? 0) > 0 ? (
            <ImageGrid items={entry.images || []} caption={'\u672c\u6761\u9644\u56fe\uff08\u91d1\u6570\u636e\u63d0\u4ea4\uff09'} />
          ) : null}
        </div>
        )
      ) : null}
    </Modal>
  );
}
